/**
 * 작성자 : 홍선영
 * 날짜 : 2024.01.05
 * 경로 : 설정관리-장비관리 페이지-비콘관리탭 추가 (현장관리자)
 */

import { useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import { useRecoilValue } from 'recoil';
import { useOutletContext } from 'react-router-dom';
import styled from 'styled-components';

import i18n from '../../../translation/i18n';
import { COMCD_USE_YN, INIT_USE_YN_A, INIT_USE_YN_Y } from '../../../_constants';
import { IModal } from 'customTypes';
import { userState } from '../../../atoms';
import { SearchOptions } from '../../../assets/styles/SearchOptions';
import { BtnBlue, BtnGhost, BtnRed } from '../../../components/Button';
import Input from '../../../components/Input';
import SelectBox from '../../../components/SelectBox';
import { apiDelete, apiGet, apiPost } from '../../../services/_common';
import { useQuery } from '@tanstack/react-query';
import TuiGrid from '../../../components/Table/TuiGrid';
import BackButton from '../../../components/BackButton';
import { useSetAuth } from '../../../utils/useSetAuth';
import { trimObject } from '../../../utils/trimObject';
import { toast } from 'react-toastify';
import Portal from '../../../components/Portal';
import DeleteModal from '../../../components/Modal/DeleteModal2';
import { ynFilter } from '../../../utils/ynFilter';
import { applyBorderStyle } from '../../../utils/applyBorderStyle';
import { useFetchCommonCodeList } from '../../../services/useSetCodeListInSelectBoxForm';
import { logPost } from '../../../services/log';
import useTuiGridSize from '../../../hooks/useTuiGridSize';

const Root = styled.div`
  &.hideRoot {
    visibility: hidden;
    position: absolute;
  }
  .tui-container {
    flex-grow: 1;
    flex-shrink: 1;
    overflow: hidden;
    margin-left: 0.5rem;
  }
  .tableWrapper {
    flex-grow: 1;
    overflow: auto;
    display: flex;
    flex-direction: column;
  }
  input[type='checkbox'] {
    margin: 0;
    width: 1rem;
    height: 1rem;
  }
  .inputFormsWrapper {
    border-bottom: 1px solid var(--outline);
    padding: 0.5rem;
    width: 100%;
    flex-grow: 0;
    flex-shrink: 0;
    .table {
      height: fit-content;
    }
  }
  .inputFormsWrapper.smallTab {
    flex-direction: row;
    align-items: center;
    padding: 0 0.5rem;
    font-weight: 500;
    &::-webkit-scrollbar {
      -webkit-appearance: none;
      height: 0rem !important;
    }
    .tab {
      color: var(--text-primary);
      display: flex;
      align-items: center;
      padding: 0 0.5rem;
      cursor: pointer;
      height: 3.5rem;
      border-bottom: 2px solid transparent;
    }
    .activeTab {
      font-weight: 700;
      color: rgba(0, 0, 0, 0.8);
      color: var(--selected-primary);
      border-bottom: 2px solid var(--selected-primary);
    }
  }
  .formTitle {
    padding: 0 0.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    font-weight: 500;
    color: var(--text-primary);
  }
  .end {
    justify-content: end;
    > button {
      margin-left: 1rem;
    }
  }

  .thead > .tr,
  .tbody > .tr {
    > div {
      display: flex;
      align-items: center;
      padding: 0 0.5rem;
    }
    .spaceBetween {
      justify-content: space-evenly;
    }
  }
  .grid {
    display: grid;
    margin-bottom: 1rem;
    align-items: center;
    min-height: 3.4rem;
  }

  .grid100,
  .grid100T,
  .grid60,
  .grid50,
  .grid30 {
    > div > div {
      display: grid;
      grid-template-columns: 9rem 1fr;
      align-items: center;
    }
  }

  .grid100T {
    grid-template-columns: 9rem 1fr;
  }

  .grid50,
  .grid60 {
    grid-template-columns: 1fr 1fr;
  }

  .grid50 {
    grid-gap: 1rem;
  }

  .grid30 {
    grid-template-columns: 1.5fr 0.7fr 0.8fr;
  }

  .plainText {
    grid-template-columns: 9rem 1fr;

    > :last-child {
      display: block;
    }
  }

  .marginBottomNone {
    margin-bottom: 0;
  }

  .emptyData {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    user-select: none;
    padding-bottom: 4rem;
    img {
      width: 16rem;
      /* user-drag: none; */
      -webkit-user-drag: none;
    }
    p {
      color: var(--text-primary);
    }
  }
`;

const SubRoot = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: fit-content;
  overflow: auto;
  justify-content: flex-start;

  .emptyData {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    user-select: none;
    padding-bottom: 4rem;
    gap: 1rem;
    img {
      width: 24rem;
      /* user-drag: none; */
      -webkit-user-drag: none;
    }
    span {
      color: var(--filled-violet);
    }
  }

  .inputForm {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
    width: 100%;
    button {
      height: 2.5rem;
      font-size: 0.75rem;
      flex-shrink: 0;
      padding: 0 0.75rem;
      border-radius: 0.25rem;
    }
    label {
      width: 6rem;
      font-weight: 500;
      flex-shrink: 0;
      font-size: 0.875rem;
      white-space: break-spaces;
      line-height: 1.4;
    }
    .inputForm-group-1280 {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      @media (min-width: 1280px) {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 2rem;
      }
    }
    .inputForm-group-1536 {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      @media (min-width: 1536px) {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 2rem;
      }
    }
    .inputForm-group-1536.withBtn {
      flex-direction: row;
      gap: 0.5rem;
      button {
        transition: none;
      }
      @media (min-width: 1536px) {
        gap: 2rem;
        button {
          margin-left: 0;
          width: fit-content;
        }
      }
    }
    .inputForm-group-1536.withBtn.inputColumnFour {
      input {
        padding: 0;
      }
    }
    .inputForm-row.labelInInput {
      display: flex;
      gap: 0.5rem;
      flex-grow: 1;
      > div {
        flex-grow: 1;
      }
    }
    .inputForm-row.labelOutInput,
    .inputForm-row.labelInInput > div > div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.5rem;
      input {
        flex-shrink: 1;
        height: 2.5rem;
        font-size: 0.875rem;
        padding: 0 0.5rem;
        &:disabled {
          background-color: var(--tonal);
          color: var(--text-disabled);
        }
      }
      > div.viewOnly {
        height: 2.5rem;
        font-size: 0.875rem;
        color: var(--text-primary);
        background-color: rgba(0, 0, 0, 0.05);
        border-radius: 0.25rem;
        padding: 0 0.75rem;
        display: flex;
        align-items: center;
      }
      > div {
        flex-grow: 1;
        > ul {
          height: 2.5rem;
          li {
            display: flex;
            width: 100%;
            max-width: 100%;
            span {
              flex-grow: 1;
              width: 4rem;
            }
          }
        }
      }
    }
    .detailInfo-group {
      margin: 1rem 0;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;
      @media (min-width: 1536px) {
        grid-template-columns: repeat(3, 1fr);
      }
      .flex-col.detailInfo {
        flex-grow: 1;
        width: 100%;
        label {
          width: 100%;
        }
        div {
          height: 2.5rem;
          color: var(--text-tertiary);
          background-color: var(--tonal);
          border-radius: 0.25rem;
          display: flex;
          align-items: center;
          padding: 0 0.5rem;
          font-size: 0.875rem;
        }
      }
    }
  }
  input {
    :disabled {
      background-color: #eeeeee;
    }
  }
  .required {
    &::after {
      content: '*';
      padding: 0 0.2rem;
      color: red;
    }
  }

  .dotBox {
    gap: 1rem;
    border-radius: 0.475rem;
    padding: 1rem;
    &.blue {
      background-color: var(--tonal-blue);
    }
    &.yellow {
      background-color: var(--tonal-orange);
    }
  }

  @media screen and (max-width: 1024px) {
  }

  @media screen and (max-width: 767px) {
    width: 100%;
    height: fit-content;
    overflow: none;
  }
`;

const ButtonsWrapper = styled.div`
  border-top: 1px solid var(--outline);
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 0.5rem;
  button {
    height: 2.5rem;
    font-size: 0.875rem;
  }
`;

const initialState = {
  bigo: '',
  bcsCd: '',
  bcsId: '',
  bcsName: '',
  useYn: '',
  tatCd: '',
  tatName: '',
  tatGubun: '',
  tadCd: '',
  tadName: '',
  tadUseYn: '',
  tadDelYn: '',
  writer: '',
  wDate: '',
  editor: '',
  eDate: '',
};

const BeaconSetting = () => {
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const size = useOutletContext<any>();
  const userInfo = useRecoilValue(userState);
  const [searchOption, setSearchOption] = useState({ bcsId: '' });
  const [searchOptionUseYn, setSearchOptionUseYn] = useState(INIT_USE_YN_A);
  const [viewTable, setViewTable] = useState<boolean>(true);
  const [columns, setColumns] = useState<any[]>([]);
  const [tableState, setTableState] = useState<any[]>([]);
  const [initTableState, setInitTableState] = useState<any[]>([]);
  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);
  const [selectedRowKey, setSelectedRowKey] = useState<any>(null);
  const [isNewAdd, setIsNewAdd] = useState(true); // 신규등록 여부
  const [isSaveClicked, setIsSaveClicked] = useState(false); // 저장버튼 클릭 여부에 따라 필수입력값 보더색상 처리하기 위한 state 값
  const [rowState, setRowState] = useState<any>(initialState);
  const [useYn, setUseYn] = useState(INIT_USE_YN_Y);
  const [openModal, setOpenModal] = useState<IModal>({ status: false, type: '', title: '' });
  const { data: useYnComCdListWithAll } = useFetchCommonCodeList(COMCD_USE_YN, true); // 사용여부 공통코드 목록 (전체포함)
  const { data: useYnComCdList } = useFetchCommonCodeList(COMCD_USE_YN, false); // 사용유무 공통코드 목록

  useEffect(() => {
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '장비 관리 > 비콘 관리',
      action: '조회',
      etc: ``,
    });
  }, []);

  const beaconListQuery = useQuery(['bcsGet', userInfo.hCd, userInfo.sCd], () => apiGet({ path: '/beacon/bcs', req: { hCd: userInfo.hCd, sCd: userInfo.sCd } }), {
    enabled: !!userInfo.hCd && !!userInfo.sCd && userInfo.sCd !== '00000',
  });

  useEffect(() => {
    if (beaconListQuery.isSuccess && beaconListQuery.data.status === 200) {
      const { data } = beaconListQuery.data.data;
      const newArray = data.map((el: any) => ({ ...el, useYnCdName: el.useYn === 'Y' ? t('사용') : t('미사용') }));
      setTableState(newArray);
      setInitTableState(newArray);
    }
  }, [beaconListQuery.isSuccess, beaconListQuery.isRefetching]);

  useEffect(() => {
    setSearchOptionUseYn(INIT_USE_YN_A);

    setColumns([
      { header: t('코드'), name: 'bcsCd', align: 'center', sortable: true, width: 80, renderer: { classNames: ['text_secondary', 'font_semibold'] } },
      { header: t('비콘 스캐너명'), name: 'bcsName', sortable: true, minWidth: 200, renderer: { classNames: ['text_secondary', 'font_semibold'] } },
      { header: t('비콘 스캐너 ID'), name: 'bcsId', sortable: true, minWidth: 200, renderer: { classNames: ['text_secondary', 'font_semibold'] } },
      { header: t('비고'), name: 'bigo', sortable: true, minWidth: 120 },
      { header: t('사용유무'), name: 'useYnCdName', align: 'center', sortable: true, minWidth: 100 },
    ]);
  }, [i18n.language]);

  useEffect(() => {
    // setSelectedRowKey(null);
    // 필터링된 어레이 리턴, 대소문자구분X
    const filteredArray = initTableState.filter((item: any) => item.bcsId?.toLowerCase()?.includes(searchOption.bcsId?.toLowerCase()));
    const result = ynFilter(filteredArray, 'useYn', searchOptionUseYn[COMCD_USE_YN]);

    if (result.length > 0) setTableState(result.map((el: any, i) => ({ ...el, rowKey: i, sortKey: i })));
    else setTableState([]);
  }, [searchOption.bcsId, searchOptionUseYn[COMCD_USE_YN], initTableState]);

  const onClickInitiateSearchOption = () => {
    setSearchOption({ bcsId: '' });
    setSearchOptionUseYn(INIT_USE_YN_A);
    onClickNewRegistration(); // 우측 인풋창도 초기화
    setSelectedRowKey(null);
    setTableState(initTableState.map((el: any, i) => ({ ...el, rowKey: i, sortKey: i })));
  };

  // 신규등록 버튼 클릭해서 새 입력창(iputForm) 노출
  const onClickNewRegistration = () => {
    setIsNewAdd(true);
    setIsSaveClicked(false);
    initiateState();
    if (size.innerSize.W <= 1024) setViewTable(false);

    setRowState(initialState);
  };

  // 스테이트 객체의 밸류를 ''로 초기화
  const initiateState = () => {
    Object.keys(rowState).map((el: any) => setRowState((prev: any) => ({ ...prev, [el]: '' })));
    setIsSaveClicked(false);
    setUseYn(INIT_USE_YN_Y);
    setSearchOptionUseYn(INIT_USE_YN_A);
    setSelectedRowKey(null);
  };

  // Row 클릭
  const onClickRow = (rowData: any, columnName: any, filterRowKey: any) => {
    const find = tableState.find((el: any) => el.rowKey === Number(filterRowKey));
    if (find) {
      // const { rowKey, rowSpanMap, sortKey, uniqueKey, _attributes, _disabledPriority, _relationListItemMap, ...newRowData } = rowData;
      setRowState(find);
      setSelectedRowKey(find.rowKey);
      setUseYn({ type: COMCD_USE_YN, [COMCD_USE_YN]: find.useYn, cdName: '' });
    }
    setIsNewAdd(false);
    setIsSaveClicked(false);
    setViewTable(false);
  };

  const backToMain = () => {
    setViewTable(true);
    setSelectedRowKey(null);
  };

  const deleteAPI = async () => {
    const req = {
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      tatCd: rowState.tatCd,
      tadCd: rowState.tadCd,
      bcsCd: rowState.bcsCd,
      editor: userInfo.userId,
    };

    const res = await apiDelete({ path: '/beacon/bcs', req });
    const { data, statusCode, message } = res.data;
    if (statusCode === 200) {
      setIsSaveClicked(false);
      setIsNewAdd(true);
      initiateState();
      const newArray = data.map((el: any) => ({ ...el, useYnCdName: el.useYn === 'Y' ? t('사용') : t('미사용') }));
      setTableState(newArray);
      setInitTableState(newArray);
      toast.success(t(message));
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '장비 관리 > 비콘 관리',
        action: '비콘 스캐너 삭제',
        etc: `비콘 스캐너명 : ${rowState.bcsName}`,
      });
    } // else toast.error(t(ERROR));
  };

  const onClickDelete = () => {
    setOpenModal((prev) => ({ ...prev, status: true, type: 'delete', api: deleteAPI }));
  };

  const onClickSave = async () => {
    setIsSaveClicked(true);
    const reqData = {
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      bcsCd: rowState.bcsCd,
      bcsId: rowState.bcsId,
      bcsName: rowState.bcsName,
      bigo: rowState.bigo,
      useYn: useYn[COMCD_USE_YN],
      delYn: 'N',
      editor: userInfo.userId,
      ...(isNewAdd && { writer: userInfo.userId }),
    };

    if (rowState.bcsId?.trim() === '' || rowState.bcsName?.trim() === '') toast.warning(t('필수입력값을 모두 입력하세요'));
    else postAPI(reqData);
  };

  const postAPI = async (obj: any) => {
    const req = trimObject(obj);
    const res = await apiPost({ path: '/beacon/bcs', req });
    const { data, statusCode, message } = res.data;
    if (statusCode === 200) {
      if (isNewAdd) setRowState(data[data.length - 1]);
      else {
        const findBcs = data.find((el: any) => rowState.bcsCd === el.bcsCd);
        if (findBcs) {
          setRowState(findBcs);
          setSelectedRowKey(rowState.rowKey);
        }
      }

      setIsSaveClicked(false);
      setIsNewAdd(false);
      const newArray = data.map((el: any, i: number) => ({ ...el, rowKey: i, sortKey: i, useYnCdName: el.useYn === 'Y' ? t('사용') : t('미사용') }));
      // setTableState(newArray);
      setInitTableState(newArray);
      toast.success(t(message));
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '장비 관리 > 비콘 관리',
        action: isNewAdd ? '비콘 스캐너 등록' : '비콘 스캐너 수정',
        etc: `비콘 스캐너명 : ${rowState.bcsName}`,
      });
    }
  };

  return (
    <div className={`content-container ${size.innerSize.W > 1024 ? 'twoColumn column-55 max800' : 'oneColumn'}`}>
      <Root className={size.innerSize.W > 1024 || viewTable ? 'showRoot' : 'hideRoot'}>
        <SearchOptions align='left'>
          {size.innerSize.W > 1024 ? (
            <>
              <div className='inputsWrapper'>
                <div className='inputForm-row'>
                  <div className='inputForm-col withLabelComCf'>
                    <label htmlFor='useYn'>{t('사용유무')}</label>
                    <SelectBox
                      options={useYnComCdListWithAll}
                      defaultOption={searchOptionUseYn.cdName}
                      state={searchOptionUseYn}
                      setState={setSearchOptionUseYn}
                      stateKey={COMCD_USE_YN}
                      initiateKey={searchOptionUseYn[COMCD_USE_YN]}
                      filterbar='filter-1-center'
                    />
                  </div>
                </div>
                <div className='inputForm-row'>
                  <div className='inputForm-col'>
                    <Input placeholder={t('비콘 스캐너 ID')} label='' type='text' id='bcsId' name='bcsId' state={searchOption} setState={setSearchOption} />
                  </div>
                </div>
              </div>
              <div className='inputsWrapper'>
                <div className='secondSearchOption'>
                  <div className='flex-basic textBtnGroup'>
                    <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                  </div>
                  <div className='flex-basic iconBtnGroup'>
                    <BtnGhost onClick={onClickNewRegistration}>
                      <span className='material-symbols-rounded'>add</span>
                      {t('등록')}
                    </BtnGhost>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className='filter-wrapper'>
              <div className='inputsWrapper'>
                <div className='inputForm-row' style={{ width: '100%' }}>
                  <div className='inputForm-col'>
                    <Input placeholder={t('비콘 스캐너 ID')} label='' type='text' id='bcsId' name='bcsId' state={searchOption} setState={setSearchOption} ico='search' />
                  </div>
                </div>
              </div>
              <div className='inputsWrapper'>
                <div className='flex-basic iconBtnGroup'>
                  <BtnGhost onClick={onClickNewRegistration}>
                    <span className='material-symbols-rounded'>add</span>
                    {t('등록')}
                  </BtnGhost>
                </div>
              </div>
            </div>
          )}
        </SearchOptions>
        <div ref={componentRef} className='tui-container'>
          <TuiGrid data={tableState} columns={columns} perPage={15} rowKey={selectedRowKey} onClickRow={onClickRow} frozenCount={1} height={tuiHeight} />
        </div>
      </Root>
      {(size.innerSize.W > 1024 || !viewTable) && (
        <Root>
          <div className='inputFormsWrapper flexRowEm'>
            {size.innerSize.W <= 1024 && <BackButton func={() => backToMain()} />}
            <div className='formTitle'>{t('비콘 정보')}</div>
          </div>
          <SubRoot>
            <div className='inputForm'>
              <div className='inputForm-group-1280'>
                {!isNewAdd && (
                  <div className='inputForm-row labelOutInput'>
                    <label htmlFor='code'>{t('코드')}</label>
                    <div className='viewOnly'>{rowState.bcsCd}</div>
                  </div>
                )}
                <div className='inputForm-row labelOutInput'>
                  <label htmlFor='useYn'>{t('사용유무')}</label>
                  <SelectBox
                    options={useYnComCdList}
                    defaultOption={useYn.cdName || t('사용')}
                    state={useYn}
                    setState={setUseYn}
                    stateKey={COMCD_USE_YN}
                    initiateKey={useYn[COMCD_USE_YN]}
                    filterbar='filter-1-left'
                  />
                </div>
              </div>
              <div className='inputForm-group-1280'>
                <div className='inputForm-row labelInInput'>
                  <Input
                    className='required'
                    label={t('비콘 스캐너명')}
                    type='text'
                    id='bcsName'
                    name='bcsName'
                    state={rowState}
                    setState={setRowState}
                    disabled={!auth.updateAuth}
                    getBorderStyle={isSaveClicked ? applyBorderStyle(rowState.bcsName, 'red', 'bcsName') : undefined}
                  />
                </div>
                <div className='inputForm-row labelInInput'>
                  <Input
                    className='required'
                    label={t('비콘 스캐너 ID')}
                    type='text'
                    id='bcsId'
                    name='bcsId'
                    state={rowState}
                    setState={setRowState}
                    disabled={!auth.updateAuth}
                    getBorderStyle={isSaveClicked ? applyBorderStyle(rowState.bcsId, 'red', 'bcsId') : undefined}
                    placeholder={t('영문키 변경 후 입력')}
                  />
                </div>
              </div>
              {rowState?.tatCd && (
                <div className='inputForm-row labelOutInput'>
                  <label htmlFor='tadInfo'>{t('터널 구역 설치 정보')}</label>
                  <div className='flexColumn dotBox blue'>
                    <div className='inputForm-row labelOutInput'>
                      <label htmlFor='protocol'>{t('구역 명')}</label>
                      <div>{rowState.tatName}</div>
                    </div>
                    <div className='inputForm-row labelOutInput'>
                      <label htmlFor='protocol'>{t('상세구역 명')}</label>
                      <div>{rowState.tadName}</div>
                    </div>
                    <div className='inputForm-row labelOutInput'>
                      <label htmlFor='protocol'>{t('운영여부')}</label>
                      <div>{rowState.tadDelYn === 'Y' ? t('삭제') : rowState.useYn === 'Y' ? t('운영중') : t('미운영')}</div>
                    </div>
                  </div>
                </div>
              )}
              <div className='inputForm-row labelInInput'>
                <Input label={t('비고')} type='text' id='bigo' name='bigo' state={rowState} setState={setRowState} disabled={!auth.updateAuth} maxLength={100} />
              </div>
              {!isNewAdd ? (
                <div className='detailInfo-group'>
                  <div className='flex-col detailInfo'>
                    <label htmlFor='createDate'>{t('등록일자')}</label>
                    <div>{rowState?.wDate}</div>
                  </div>
                  <div className='flex-col detailInfo'>
                    <label htmlFor='createUser'>{t('등록자')}</label>
                    <div>{rowState.writer}</div>
                  </div>
                  <div className='flex-col detailInfo'>
                    <label htmlFor='updateDate'>{t('수정일자')}</label>
                    <div>{rowState.eDate}</div>
                  </div>
                  <div className='flex-col detailInfo'>
                    <label htmlFor='updateUser'>{t('수정자')}</label>
                    <div>{rowState.editor}</div>
                  </div>
                </div>
              ) : undefined}
            </div>
          </SubRoot>
          <ButtonsWrapper>
            {!isNewAdd && auth.deleteAuth && <BtnRed onClick={onClickDelete}>{t('삭제')}</BtnRed>}
            {(auth.createAuth || auth.updateAuth) && <BtnBlue onClick={onClickSave}>{t('저장')}</BtnBlue>}
          </ButtonsWrapper>
        </Root>
      )}
      <Portal openModal={openModal?.status}>{openModal && openModal.type === 'delete' ? <DeleteModal openModal={openModal} setOpenModal={setOpenModal} /> : undefined}</Portal>
    </div>
  );
};

export default BeaconSetting;
